import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileUp, AlertTriangle, CheckCircle2, XCircle, RotateCcw, Loader2,
  Sparkles, Plus, ChevronDown, ChevronRight, Play,
} from "lucide-react";

const fmtTRY = (n) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";

const CONF_CLS = (c) => {
  if (c >= 95) return "bg-emerald-100 text-emerald-800 border-emerald-500";
  if (c >= 70) return "bg-yellow-100 text-yellow-800 border-yellow-500";
  if (c >= 40) return "bg-orange-100 text-orange-800 border-orange-500";
  return "bg-rose-100 text-rose-800 border-rose-500";
};

/**
 * Toplu XML fatura içe aktarma sayfası.
 * - Kullanıcı 1 veya N XML seçer → Promise.all ile paralel parse
 * - Her fatura bağımsız bir Job olarak render edilir (accordion)
 * - Her Job: yön seçici + duplicate kontrolü + satır kararları + Uygula
 * - "Uygula Tümü": hazır (parse ok, applied değil, duplicate onaylı) job'ları sırayla apply eder
 */
export default function InvoiceImports() {
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [batchApplying, setBatchApplying] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/invoice-imports"); setHistory(data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onParseAll = async () => {
    if (!files.length) { toast.error("En az bir XML dosyası seçin"); return; }
    setParsing(true);
    const promises = files.map(async (file) => {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/invoice-imports/parse", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        return {
          id: `${file.name}-${Math.random().toString(36).slice(2, 7)}`,
          file, ok: true, parsed: data,
          direction: data.suggested_direction || "purchase",
          decisions: (data.lines || []).map((ln) => ({
            action: ln.suggestion?.suggested_stock_id ? "existing_stock" : "skip",
            stock_id: ln.suggestion?.suggested_stock_id || "",
            new_stock: null,
            save_alias: !!ln.code && !!(data.parsed?.supplier_vkn || data.parsed?.customer_vkn),
          })),
          createInvoice: true,
          duplicateConfirmed: false,
          expanded: files.length === 1,   // tek dosyada otomatik açık
          applied: false, applying: false, error: null,
        };
      } catch (e) {
        return {
          id: `${file.name}-err-${Math.random().toString(36).slice(2, 5)}`,
          file, ok: false,
          error: e?.response?.data?.detail || e?.message || "Bilinmeyen hata",
        };
      }
    });
    const results = await Promise.all(promises);
    setJobs(results);
    setParsing(false);
    const okCount = results.filter((r) => r.ok).length;
    toast.success(`${okCount}/${results.length} fatura parse edildi`);
  };

  const updateJob = (id, patch) => {
    setJobs((prev) => prev.map((j) => j.id === id ? { ...j, ...patch } : j));
  };

  const applyOne = async (id) => {
    const job = jobs.find((j) => j.id === id);
    if (!job || !job.ok || job.applied) return { ok: false, reason: "invalid" };
    if (job.parsed.duplicate && !job.duplicateConfirmed) return { ok: false, reason: "duplicate" };
    updateJob(id, { applying: true, error: null });
    try {
      const p = job.direction === "sales" ? job.parsed.customer : job.parsed.supplier;
      const lines = job.parsed.lines.map((ln, i) => {
        const d = job.decisions[i];
        return {
          code: ln.code, name: ln.name, quantity: ln.quantity,
          unit_price_ex_vat: ln.unit_price_ex_vat, unit: ln.unit,
          total_ex_vat: ln.total_ex_vat, vat_rate: ln.vat_rate,
          action: d.action,
          stock_id: d.action === "existing_stock" ? d.stock_id : undefined,
          new_stock: d.action === "new_stock" ? d.new_stock : undefined,
          save_alias: !!d.save_alias,
        };
      });
      const payload = {
        ettn: job.parsed.parsed.ettn,
        invoice_number: job.parsed.parsed.invoice_number,
        invoice_date: job.parsed.parsed.invoice_date,
        total_amount: job.parsed.parsed.total_amount,
        vat_amount: job.parsed.parsed.vat_amount,
        direction: job.direction,
        party: { name: p?.name, vkn: p?.vkn, contact_id: p?.contact_id },
        lines,
        create_invoice: job.createInvoice,
        force: !!job.parsed.duplicate,
        filename: job.file?.name,
      };
      await api.post("/invoice-imports/apply", payload);
      updateJob(id, { applying: false, applied: true });
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      updateJob(id, { applying: false, error: msg });
      return { ok: false, reason: msg };
    }
  };

  const applyAll = async () => {
    setBatchApplying(true);
    const ready = jobs.filter((j) => j.ok && !j.applied && (!j.parsed.duplicate || j.duplicateConfirmed));
    let ok = 0, fail = 0;
    for (const j of ready) {
      const r = await applyOne(j.id);
      if (r.ok) ok++; else fail++;
    }
    setBatchApplying(false);
    if (ok) toast.success(`${ok} fatura uygulandı`);
    if (fail) toast.error(`${fail} fatura uygulanamadı — detay için satırlara bakın`);
    loadHistory();
  };

  const reset = () => { setFiles([]); setJobs([]); };

  const stats = useMemo(() => {
    const parsed = jobs.filter((j) => j.ok);
    const applied = parsed.filter((j) => j.applied);
    const duplicates = parsed.filter((j) => j.parsed?.duplicate);
    const errors = jobs.filter((j) => !j.ok);
    const totalAmount = parsed.reduce((s, j) => s + (j.parsed?.parsed?.total_amount || 0), 0);
    return { total: jobs.length, parsed: parsed.length, applied: applied.length, errors: errors.length, duplicates: duplicates.length, totalAmount };
  }, [jobs]);

  const readyCount = jobs.filter((j) => j.ok && !j.applied && (!j.parsed.duplicate || j.duplicateConfirmed)).length;

  const onDelete = async (iid) => {
    if (!window.confirm("Bu içe aktarımı geri alalım mı? Tüm stok hareketleri geri düşer.")) return;
    try { await api.delete(`/invoice-imports/${iid}`); toast.success("Geri alındı"); loadHistory(); }
    catch (e) { toast.error("Silinemedi: " + (e?.response?.data?.detail || "")); }
  };

  return (
    <div className="space-y-8" data-testid="invoice-imports-page">
      <header>
        <p className="label-caps">Fatura İçe Aktar</p>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
          <FileUp size={28} /> e-Fatura XML → Stok / Satış (Toplu)
        </h1>
        <p className="text-xs text-neutral-500 mt-1.5 max-w-2xl">
          Bir veya birden çok UBL-TR XML dosyasını aynı anda yükleyin. Sistem her birini paralel parse eder, akıllı stok eşleştirmesi yapar, satış/alış yönünü otomatik önerir. Onaylayıp toplu uygulayabilirsiniz.
        </p>
      </header>

      {jobs.length === 0 && (
        <div className="brutal-card p-6 bg-white space-y-4">
          <label className="block border-2 border-dashed border-black/30 p-8 text-center cursor-pointer hover:bg-neutral-50">
            <input
              data-testid="ii-file-input"
              type="file" accept=".xml,application/xml,text/xml" multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="hidden"
            />
            <FileUp size={32} className="mx-auto text-neutral-400" />
            <div className="mt-2 text-sm font-medium">
              {files.length === 0
                ? "XML dosyalarını seçin (birden fazla seçebilirsiniz)"
                : `${files.length} dosya seçildi`}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              UBL-TR uyumlu (.xml) · dosya başına en fazla 5 MB · shift/ctrl ile çoklu seçim
            </div>
          </label>
          {files.length > 0 && (
            <div className="text-xs bg-neutral-50 border border-black/10 p-3 max-h-40 overflow-y-auto space-y-0.5" data-testid="ii-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex justify-between font-mono-num">
                  <span className="truncate">{f.name}</span>
                  <span className="text-neutral-500">{(f.size / 1024).toFixed(1)} KB</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={() => setFiles([])} disabled={!files.length}
                    className="text-xs underline text-neutral-500 disabled:opacity-30">Temizle</button>
            <button
              data-testid="ii-parse-btn"
              disabled={!files.length || parsing}
              onClick={onParseAll}
              className="brutal-btn bg-black text-white px-6 py-2.5 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
            >
              {parsing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {parsing ? "Parse ediliyor…" : `Yükle & Analiz Et${files.length > 1 ? ` (${files.length})` : ""}`}
            </button>
          </div>
        </div>
      )}

      {jobs.length > 0 && (
        <>
          {/* Toplu özet */}
          <div className="brutal-card p-4 bg-white flex flex-wrap items-center gap-3 sm:gap-4" data-testid="ii-batch-summary">
            <StatChip label="Toplam" value={stats.total} />
            <StatChip label="Parse OK" value={stats.parsed} tone="emerald" />
            {stats.duplicates > 0 && <StatChip label="Duplike" value={stats.duplicates} tone="yellow" />}
            {stats.errors > 0 && <StatChip label="Hata" value={stats.errors} tone="rose" />}
            <StatChip label="Uygulandı" value={stats.applied} tone="emerald" />
            <div className="text-xs text-neutral-500 ml-auto">
              Toplam tutar: <b className="font-mono-num text-black">{fmtTRY(stats.totalAmount)}</b>
            </div>
            <button onClick={reset} className="text-xs underline text-neutral-500 hover:text-black" data-testid="ii-batch-reset">
              Baştan Başla
            </button>
            <button
              onClick={applyAll}
              disabled={readyCount === 0 || batchApplying}
              data-testid="ii-batch-apply"
              className="brutal-btn bg-black text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
            >
              {batchApplying ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              {batchApplying ? "Uygulanıyor…" : `Tümünü Uygula (${readyCount})`}
            </button>
          </div>

          {/* Job kartları */}
          <div className="space-y-3">
            {jobs.map((job) => (
              <JobCard
                key={job.id} job={job}
                onToggle={() => updateJob(job.id, { expanded: !job.expanded })}
                onDirectionChange={(d) => updateJob(job.id, { direction: d })}
                onConfirmDuplicate={() => updateJob(job.id, { duplicateConfirmed: true })}
                onDecisionChange={(idx, patch) => {
                  const next = [...job.decisions];
                  next[idx] = { ...next[idx], ...patch };
                  updateJob(job.id, { decisions: next });
                }}
                onToggleCreateInvoice={(v) => updateJob(job.id, { createInvoice: v })}
                onApply={() => applyOne(job.id).then((r) => {
                  if (r.ok) { toast.success(`Uygulandı: ${job.file?.name}`); loadHistory(); }
                  else toast.error(r.reason || "Uygulanamadı");
                })}
              />
            ))}
          </div>
        </>
      )}

      {/* Geçmiş */}
      {history.length > 0 && (
        <div className="brutal-card overflow-hidden bg-white" data-testid="ii-history">
          <div className="p-3 border-b-2 border-black/80 bg-neutral-50 label-caps">
            Geçmiş İçe Aktarımlar ({history.length})
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/30 text-xs">
                <th className="text-left px-3 py-2 label-caps">Tarih</th>
                <th className="text-center px-3 py-2 label-caps">Yön</th>
                <th className="text-left px-3 py-2 label-caps">Fatura No</th>
                <th className="text-left px-3 py-2 label-caps">Cari</th>
                <th className="text-right px-3 py-2 label-caps">Toplam</th>
                <th className="text-center px-3 py-2 label-caps">Satır</th>
                <th className="text-center px-3 py-2 label-caps">Fatura</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`ii-history-row-${h.id}`}>
                  <td className="px-3 py-2 font-mono-num text-xs">{h.invoice_date}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                      h.direction === "sales" ? "bg-rose-100 text-rose-800 border-rose-400" : "bg-emerald-100 text-emerald-800 border-emerald-400"
                    }`}>
                      {h.direction === "sales" ? "Satış" : "Alış"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs">{h.invoice_number}</td>
                  <td className="px-3 py-2">{h.party_name}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtTRY(h.total_amount)}</td>
                  <td className="px-3 py-2 text-center text-xs">{(h.stock_movement_ids || []).length}</td>
                  <td className="px-3 py-2 text-center text-xs">{h.invoice_id ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDelete(h.id)} data-testid={`ii-history-delete-${h.id}`}
                            className="p-1.5 hover:bg-rose-50" title="Geri al">
                      <RotateCcw size={13} className="text-rose-600" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.4rem 0.6rem;outline:none;font-size:0.8rem;background:white}
                  .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const cls = { emerald: "bg-emerald-100 text-emerald-800", yellow: "bg-yellow-100 text-yellow-800",
                rose: "bg-rose-100 text-rose-800" }[tone] || "bg-neutral-100 text-neutral-800";
  return (
    <div className={`${cls} border border-black/10 px-2.5 py-1`}>
      <span className="text-[10px] uppercase tracking-widest">{label}</span>
      <span className="ml-1.5 font-mono-num font-bold">{value}</span>
    </div>
  );
}

function JobCard({ job, onToggle, onDirectionChange, onConfirmDuplicate, onDecisionChange, onToggleCreateInvoice, onApply }) {
  // Error state
  if (!job.ok) {
    return (
      <div className="brutal-card p-4 bg-rose-50 border-2 border-rose-500" data-testid={`ii-job-error-${job.id}`}>
        <div className="flex items-start gap-3">
          <XCircle size={18} className="text-rose-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm">{job.file?.name}</div>
            <div className="text-xs text-rose-800 mt-0.5">Parse hatası: {job.error}</div>
          </div>
        </div>
      </div>
    );
  }

  const p = job.direction === "sales" ? job.parsed.customer : job.parsed.supplier;
  const isDup = !!job.parsed.duplicate;
  const dupOk = !isDup || job.duplicateConfirmed;
  const canApply = dupOk && !job.applied && !job.applying;

  return (
    <div className={`brutal-card bg-white ${job.applied ? "border-2 border-emerald-500" : isDup ? "border-2 border-yellow-500" : ""}`}
         data-testid={`ii-job-${job.id}`}>
      {/* Başlık şeridi (tıklayınca aç/kapa) */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-neutral-50"
        data-testid={`ii-job-toggle-${job.id}`}
      >
        {job.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-num text-xs font-semibold">{job.parsed.parsed.invoice_number}</span>
            <span className="text-[11px] text-neutral-500">{job.parsed.parsed.invoice_date}</span>
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
              job.direction === "sales" ? "bg-rose-100 text-rose-800 border-rose-400" : "bg-emerald-100 text-emerald-800 border-emerald-400"
            }`}>
              {job.direction === "sales" ? "Satış" : "Alış"}
            </span>
            {isDup && (
              <span className="text-[10px] uppercase tracking-widest bg-yellow-100 text-yellow-800 px-1.5 py-0.5 border border-yellow-400">
                ⚠ Duplike
              </span>
            )}
            {job.applied && (
              <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5">
                ✓ Uygulandı
              </span>
            )}
            {job.error && (
              <span className="text-[10px] uppercase tracking-widest bg-rose-100 text-rose-800 px-1.5 py-0.5 border border-rose-400">
                ✗ {job.error.slice(0, 40)}
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-600 mt-0.5 truncate">{p?.name}</div>
        </div>
        <div className="text-right font-mono-num text-sm">
          <div className="font-bold">{fmtTRY(job.parsed.parsed.total_amount)}</div>
          <div className="text-[10px] text-neutral-500">{job.parsed.lines.length} satır</div>
        </div>
      </button>

      {job.expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-black/10">
          {/* Duplike uyarısı */}
          {isDup && (
            <div className="bg-yellow-50 border-2 border-yellow-500 p-3 mt-3" data-testid={`ii-job-dup-${job.id}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-yellow-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-xs">
                  <b>Mükerrer fatura</b> — Bu ETTN daha önce yüklendi (No: {job.parsed.duplicate.invoice_number} · {job.parsed.duplicate.invoice_date}).
                  {!job.duplicateConfirmed ? (
                    <button
                      onClick={() => {
                        if (window.confirm("EMİN MİSİNİZ? Aynı fatura zaten kayıtlı. Yine de yüklemek istiyor musunuz?")) {
                          onConfirmDuplicate();
                        }
                      }}
                      data-testid={`ii-job-dup-confirm-${job.id}`}
                      className="ml-2 border border-yellow-700 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-white hover:bg-yellow-100">
                      Yine de Yükle
                    </button>
                  ) : (
                    <span className="ml-2 text-[10px] uppercase tracking-widest bg-yellow-900 text-white px-1.5 py-0.5">✓ Onaylandı</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Yön seçici */}
          <div className="flex items-center gap-3 flex-wrap pt-3">
            <span className="label-caps text-xs">Yön:</span>
            <div className="inline-flex border-2 border-black">
              <button onClick={() => onDirectionChange("purchase")}
                      data-testid={`ii-job-dir-purchase-${job.id}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-widest ${job.direction === "purchase" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>
                Alış
              </button>
              <button onClick={() => onDirectionChange("sales")}
                      data-testid={`ii-job-dir-sales-${job.id}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border-l-2 border-black ${job.direction === "sales" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>
                Satış
              </button>
            </div>
            {job.parsed.suggested_direction === job.direction && (
              <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 border border-emerald-500">✓ Öneri</span>
            )}
            <span className="text-[11px] text-neutral-500">
              {job.direction === "sales" ? "Müşteri" : "Tedarikçi"}: <b>{p?.name || "—"}</b>
              {p?.vkn && <span className="font-mono-num text-neutral-400 ml-1">({p.vkn})</span>}
              {!p?.existing && <span className="ml-1 text-yellow-700">+ Otomatik oluşturulacak</span>}
            </span>
          </div>

          {/* Satırlar */}
          <div className="border border-black/10 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 border-b border-black/20">
                <tr>
                  <th className="text-left px-2 py-1.5 label-caps">Kod · Ad</th>
                  <th className="text-right px-2 py-1.5 label-caps">Adet</th>
                  <th className="text-right px-2 py-1.5 label-caps">B.Fiyat</th>
                  <th className="text-left px-2 py-1.5 label-caps">Öneri</th>
                  <th className="text-left px-2 py-1.5 label-caps">Karar</th>
                </tr>
              </thead>
              <tbody>
                {job.parsed.lines.map((ln, i) => (
                  <LineRow
                    key={i} idx={i} line={ln}
                    stocks={job.parsed.stocks}
                    decision={job.decisions[i]}
                    canSaveAlias={!!ln.code && !!(job.parsed.parsed.supplier_vkn || job.parsed.parsed.customer_vkn)}
                    onChange={(patch) => onDecisionChange(i, patch)}
                    jobId={job.id}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Alt aksiyon */}
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`ii-job-createinv-${job.id}`}>
              <input type="checkbox" checked={job.createInvoice}
                     onChange={(e) => onToggleCreateInvoice(e.target.checked)}
                     className="w-3.5 h-3.5 accent-black" />
              {job.direction === "sales" ? "Satış Faturası" : "Alış Faturası"} da oluştur
            </label>
            <button
              onClick={onApply}
              disabled={!canApply}
              data-testid={`ii-job-apply-${job.id}`}
              className="brutal-btn bg-black text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
            >
              {job.applying ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              {job.applied ? "Uygulandı" : job.applying ? "Uygulanıyor…" : "Uygula"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LineRow({ idx, line, stocks, decision, canSaveAlias, onChange, jobId }) {
  const sug = line.suggestion || {};
  const isHalf = !!sug.half_sheet;
  const label = sug.reason === "alias" ? "Alias"
               : sug.reason === "dim_exact" ? "Ölçü tam"
               : sug.reason === "dim_exact_half" ? "Ölçü tam · yarım levha"
               : sug.reason === "dim_tolerant" ? "Ölçü yakın"
               : sug.reason === "dim_tolerant_half" ? "Ölçü yakın · yarım levha"
               : "Bulunamadı";
  return (
    <tr className="border-b border-black/5 align-top" data-testid={`ii-row-${jobId}-${idx}`}>
      <td className="px-2 py-1.5">
        {line.code && <span className="font-mono-num text-[10px] bg-neutral-100 px-1 mr-1">{line.code}</span>}
        <span>{line.name}</span>
        {line.dims && line.dims.length > 0 && (
          <div className="text-[10px] text-neutral-500 mt-0.5">
            Ölçü: {line.dims.join(" × ")} mm
            {isHalf && (
              <span className="ml-2 inline-block px-1 py-0.5 text-[9px] uppercase tracking-widest border border-sky-400 bg-sky-50 text-sky-800 font-mono-num">
                Yarım Levha · ×2 fatura = 1 kutu
              </span>
            )}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-right font-mono-num">{line.quantity} <span className="text-neutral-500">{line.unit}</span></td>
      <td className="px-2 py-1.5 text-right font-mono-num">{fmtTRY(line.unit_price_ex_vat)}</td>
      <td className="px-2 py-1.5">
        <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${CONF_CLS(sug.confidence || 0)}`}>
          {sug.confidence || 0}% · {label}
        </span>
      </td>
      <td className="px-2 py-1.5 space-y-1">
        <select value={decision?.action || "skip"}
                onChange={(e) => onChange({ action: e.target.value })}
                data-testid={`ii-row-action-${jobId}-${idx}`}
                className="input">
          <option value="existing_stock">Mevcut stoktan seç</option>
          <option value="new_stock">Yeni stok oluştur</option>
          <option value="skip">Atla</option>
        </select>
        {decision?.action === "existing_stock" && (
          <select value={decision.stock_id || ""}
                  onChange={(e) => onChange({ stock_id: e.target.value })}
                  data-testid={`ii-row-stock-${jobId}-${idx}`}
                  className="input">
            <option value="">— stok seçin —</option>
            {stocks.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sku} · {s.name}
                {s.sheet_width_mm ? ` (${s.sheet_width_mm}×${s.sheet_length_mm}mm)` : ""}
              </option>
            ))}
          </select>
        )}
        {decision?.action === "new_stock" && (
          <NewStockForm line={line} value={decision.new_stock}
                        onChange={(ns) => onChange({ new_stock: ns })} />
        )}
        {canSaveAlias && decision?.action !== "skip" && (
          <label className="flex items-center gap-1.5 text-[10px] mt-1">
            <input type="checkbox" checked={!!decision?.save_alias}
                   onChange={(e) => onChange({ save_alias: e.target.checked })}
                   className="w-3 h-3 accent-black"
                   data-testid={`ii-row-alias-${jobId}-${idx}`} />
            Bu kodu kaydet (sonraki faturada otomatik bulsun)
          </label>
        )}
      </td>
    </tr>
  );
}

function NewStockForm({ line, value, onChange }) {
  const [ns, setNs] = useState(() => value || {
    name: line.name || "",
    box_type: "abox",
    length_mm: line.dims?.[2] || "", width_mm: line.dims?.[1] || line.dims?.[0] || "",
    height_mm: "", flute: "", quality: "", grammage: "",
    unit: line.unit || "adet", sku: "",
  });
  useEffect(() => { onChange(ns); /* eslint-disable-next-line */ }, [ns]);
  const isAbox = ns.box_type === "abox";
  return (
    <div className="border border-black/20 p-2 bg-neutral-50 space-y-1.5">
      <input placeholder="Stok adı" value={ns.name}
             onChange={(e) => setNs({ ...ns, name: e.target.value })}
             className="input" />
      <div className="grid grid-cols-2 gap-1">
        <select value={ns.box_type} onChange={(e) => setNs({ ...ns, box_type: e.target.value })} className="input">
          <option value="abox">A Box</option>
          <option value="aux">Yardımcı Malzeme</option>
        </select>
        <input placeholder="Birim" value={ns.unit}
               onChange={(e) => setNs({ ...ns, unit: e.target.value })}
               className="input" />
      </div>
      {isAbox && (
        <div className="grid grid-cols-3 gap-1">
          <input type="number" placeholder="L" value={ns.length_mm}
                 onChange={(e) => setNs({ ...ns, length_mm: e.target.value })}
                 className="input font-mono-num" />
          <input type="number" placeholder="W" value={ns.width_mm}
                 onChange={(e) => setNs({ ...ns, width_mm: e.target.value })}
                 className="input font-mono-num" />
          <input type="number" placeholder="H" value={ns.height_mm}
                 onChange={(e) => setNs({ ...ns, height_mm: e.target.value })}
                 className="input font-mono-num" />
        </div>
      )}
      <input placeholder="SKU (boş = otomatik)" value={ns.sku}
             onChange={(e) => setNs({ ...ns, sku: e.target.value })}
             className="input font-mono-num" />
    </div>
  );
}
